/**
 * 매장 실시간/예약 노출 가능 여부 판단 및 예약 노출 타입(오늘/내일/X).
 * home, store, boss 등 여러 route에서 재사용하므로 src/utils에 둔다.
 */
import type { Store } from "../__generated__/graphql";

export type ExposeType = "오늘" | "내일" | "X";

export interface PackExposeInput {
  isTodayEnabled?: boolean | null;
  isTomorrowEnabled?: boolean | null;
  isOverTodayPickupTime?: boolean | null;
}

/**
 * 팩의 오늘/내일 노출 가능 여부와 픽업 시간 경과 여부로 노출 타입을 반환한다.
 */
export function getExposeType(
  pack: PackExposeInput | null | undefined
): ExposeType {
  const { isTodayEnabled, isTomorrowEnabled, isOverTodayPickupTime } =
    pack || {};

  if (isTodayEnabled && isTomorrowEnabled) {
    return isOverTodayPickupTime ? "내일" : "오늘";
  }
  if (!isTodayEnabled && isTomorrowEnabled) {
    return "내일";
  }
  if (isTodayEnabled && !isTomorrowEnabled) {
    return isOverTodayPickupTime ? "X" : "오늘";
  }
  return "X";
}

/**
 * 실시간(바로픽업) 상품 노출 가능 여부.
 */
export function canProductExpose(store: Store): boolean {
  return !!(store.todaysProducts?.length && !store.isProductAllSoldOut);
}

/**
 * 예약 팩 노출 가능 여부 (팩·예약시간 설정 여부).
 * pack.id와 reserveTime만 사용하므로 이 필드만 있는 타입만 받음 (Store, LikedStoreItem 등 호환).
 */
export interface StoreForPackExpose {
  pack?: { id?: number | null } | null;
  reserveTime?: unknown;
}

export function canPackExpose(
  store: StoreForPackExpose | null | undefined
): boolean {
  return !!(store?.pack?.id && store?.reserveTime);
}

/** 도메인 플래그 계산용 최소 Store 형태 */
export interface StoreForFlags {
  pack?: { currentCount?: number | null } | null;
  reserveTime?: Record<string, { enabled?: boolean }> | null;
  todaysProducts?: unknown[] | null;
}

/** 팩·예약시간 설정 여부 (랜덤박스 예약 가능 여부). */
export function isPackSetted(
  store: StoreForFlags | null | undefined
): boolean {
  return !!(store?.pack && store?.reserveTime);
}

/** 당일 실시간(바로픽업) 판매 여부. */
export function isTimeSaling(
  store: StoreForFlags | null | undefined
): boolean {
  return (store?.todaysProducts?.length ?? 0) > 0;
}

/** 오늘 또는 내일 예약 시간이 활성화되어 있는지. */
export function isReserveTimeEnabled(
  reserveTime: Record<string, { enabled?: boolean }> | null | undefined,
  dayOfWeek: string,
  tomorrowDayOfWeek: string
): boolean {
  return !!(
    reserveTime?.[dayOfWeek]?.enabled ||
    reserveTime?.[tomorrowDayOfWeek]?.enabled
  );
}

/** 팩 재고 소진 여부. */
export function isPackSoldOut(
  store: StoreForFlags | null | undefined
): boolean {
  const count = store?.pack?.currentCount;
  return (
    count === undefined ||
    count === null ||
    count < 0 ||
    count === 0
  );
}

